"use client";

import { useRef } from "react";
import { useTranslations } from "next-intl";

const COLOR_ROLE_KEYS = [
  "colorRole0",
  "colorRole1",
  "colorRole2",
  "colorRole3",
] as const;

export function ColorEditor({
  colors,
  originalColors,
  paletteName,
  onChange,
  onReset,
  onReverse,
}: {
  colors: string[];
  originalColors: string[];
  paletteName: string;
  onChange: (idx: number, hex: string) => void;
  onReset: () => void;
  onReverse: () => void;
}) {
  const t = useTranslations("catalog");
  const pickerRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isCustomised = colors.some((c, i) => c !== originalColors[i]);

  return (
    <div
      className="rounded-xl border p-4"
      style={{
        borderColor: "rgba(245,245,247,0.08)",
        background: "rgba(255,255,255,0.02)",
      }}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.15em]"
            style={{ color: "#3B7EFF" }}
          >
            {t("editColors")}
          </p>
          <p
            className="truncate text-xs font-semibold"
            style={{ color: "#F5F5F7" }}
          >
            {paletteName}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <button
            onClick={onReverse}
            className="rounded-lg px-2.5 py-1.5 text-[10px] font-semibold transition-opacity hover:opacity-80"
            style={{ background: "rgba(30,63,232,0.15)", color: "#3B7EFF" }}
            title={t("reverseColors")}
          >
            ⇅ {t("reverseColors")}
          </button>
          {isCustomised && (
            <button
              onClick={onReset}
              className="rounded-lg px-2.5 py-1.5 text-[10px] font-semibold transition-opacity hover:opacity-80"
              style={{ background: "rgba(245,245,247,0.08)", color: "#8B8B94" }}
            >
              {t("resetColor")}
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {colors.map((color, i) => (
          <div key={i} className="relative flex-1">
            <button
              onClick={() => pickerRefs.current[i]?.click()}
              className="h-10 w-full rounded-lg border-2 transition-all duration-150 hover:scale-105 hover:shadow-lg"
              style={{
                background: color,
                borderColor:
                  isCustomised && color !== originalColors[i]
                    ? "rgba(30,63,232,0.7)"
                    : "rgba(245,245,247,0.12)",
              }}
              title={`${t(COLOR_ROLE_KEYS[i])}: ${color}`}
            />
            <p
              className="mt-1 text-center font-mono text-[9px]"
              style={{ color: "#8B8B94" }}
            >
              {color.toUpperCase()}
            </p>
            <p
              className="text-center text-[8px] uppercase"
              style={{ color: "#5A5A64" }}
            >
              {t(COLOR_ROLE_KEYS[i])}
            </p>
            <input
              ref={(el) => {
                pickerRefs.current[i] = el;
              }}
              type="color"
              value={color}
              onChange={(e) => onChange(i, e.target.value)}
              className="absolute inset-0 h-0 w-0 cursor-pointer opacity-0"
              aria-label={t(COLOR_ROLE_KEYS[i])}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
